<!-- src/views/AdminView.vue -->
<template>
  <div class="admin-view container">
    <h1>Админ — загрузка данных</h1>

    <!-- если не авторизованы — показываем форму логина -->
    <section v-if="!isAuthenticated" class="login-block card">
      <h2>Вход для администратора</h2>
      <form @submit.prevent="login" class="login-form">
        <div class="form-row">
          <label>Имя пользователя</label>
          <input v-model="username" type="text" required />
        </div>
        <div class="form-row">
          <label>Пароль</label>
          <input v-model="password" type="password" required />
        </div>
        <div class="form-actions">
          <button class="btn btn-primary" type="submit">Войти</button>
        </div>

        <p v-if="authError" class="error">{{ authError }}</p>
      </form>

    </section>

    <!-- админский интерфейс -->
    <section v-else class="admin-card">
      <div class="admin-toolbar">
        <div>
          <button class="btn" @click="logout">Выйти</button>
        </div>

        <div class="upload-actions">
          <label class="btn btn-primary">
            Загрузить Excel
            <input ref="fileInput" type="file" accept=".xlsx" @change="onFileSelected" style="display:none" />
          </label>

          <!-- кнопка fallback если есть компонент UploadExcel — можно оставить -->
          <!-- <UploadExcel @uploaded="onUploadSuccess" v-if="false" /> -->
        </div>
      </div>

      <div class="status-row">
        <div v-if="uploadInProgress">Загрузка... {{ uploadProgressText }}</div>
        <div v-if="uploadSuccess" class="success">Файл успешно загружен</div>
        <div v-if="uploadError" class="error">{{ uploadError }}</div>
      </div>

      <section class="items-table card" v-if="items && items.length">
        <h2>Загруженные образцы ({{ items.length }})</h2>

        <div class="table-controls">
          <input v-model="filterQuery" placeholder="Фильтр по идентификатору / basin / renamed_id" />
          <button class="btn" @click="refreshItems">Обновить</button>
        </div>

        <table>
          <thead>
            <tr>
              <th @click="setSort('id')">id <small v-if="sortKey==='id'">({{ sortDirLabel }})</small></th>
              <th @click="setSort('renamed_id')">renamed_id <small v-if="sortKey==='renamed_id'">({{ sortDirLabel }})</small></th>
              <th @click="setSort('internal_id')">internal_id <small v-if="sortKey==='internal_id'">({{ sortDirLabel }})</small></th>
              <th @click="setSort('fadr')">fadr</th>
              <th @click="setSort('basin')">basin</th>
              <th @click="setSort('department')">department</th>
              <th @click="setSort('resolution')">resolution</th>
              <th @click="setSort('porosity')">porosity</th>
              <th @click="setSort('permeability')">permeability</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="it in visibleItems" :key="it.id">
              <td>{{ it.id }}</td>
              <td>{{ it.renamed_id || '-' }}</td>
              <td>{{ it.internal_id || '-' }}</td>
              <td>{{ it.fadr || '-' }}</td>
              <td>{{ it.basin || '-' }}</td>
              <td>{{ it.department || '-' }}</td>
              <td>{{ numericOrDash(it.resolution) }}</td>
              <td>{{ numericOrDash(it.porosity) }}</td>
              <td>{{ numericOrDash(it.permeability) }}</td>
            </tr>
          </tbody>
        </table>

        <div class="paging-lite">
          <label>Показывать:
            <select v-model.number="pageSize">
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
              <option :value="10000">все</option>
            </select>
          </label>

          <div class="page-nav">
            <button class="btn" :disabled="page===1" @click="page--">◀</button>
            <span>Страница {{ page }} / {{ totalPages }}</span>
            <button class="btn" :disabled="page>=totalPages" @click="page++">▶</button>
          </div>
        </div>
      </section>

      <div v-else class="no-items hint card">
        Список образцов пуст — нажмите «Обновить» или загрузите файл
      </div>
    </section>
  </div>
</template>

<script>
/*
  AdminView.vue
  - авторизация через POST /api/v1/token/ (OAuth2PasswordRequestForm)
  - загрузка файла через POST /api/v1/items/ (Authorization: Bearer <token>)
  - получение списка items: GET /api/v1/items/?limit=10000
  - простая клиентская сортировка и фильтр
*/
export default {
  name: "AdminView",
  data() {
    return {
      username: "",
      password: "",
      isAuthenticated: false,
      token: null,
      authError: null,

      // загрузка
      uploadInProgress: false,
      uploadProgressText: "",
      uploadError: null,
      uploadSuccess: false,

      // items
      items: [],
      filterQuery: "",
      page: 1,
      pageSize: 50,
      sortKey: "id",
      sortDir: "desc" /* 'asc' or 'desc' */,
    };
  },

  computed: {
    sortDirLabel() {
      return this.sortDir === "asc" ? "↑" : "↓";
    },

    // фильтрация + сортировка + пагинация
    filteredItems() {
      if (!this.items || !this.items.length) return [];
      const q = (this.filterQuery || "").toLowerCase().trim();
      if (!q) return this.items.slice();
      return this.items.filter(it => {
        return (
          String(it.renamed_id || "").toLowerCase().includes(q) ||
          String(it.internal_id || "").toLowerCase().includes(q) ||
          String(it.basin || "").toLowerCase().includes(q) ||
          String(it.fadr || "").toLowerCase().includes(q)
        );
      });
    },

    sortedItems() {
      const arr = this.filteredItems.slice();
      const key = this.sortKey;
      const dir = this.sortDir === "asc" ? 1 : -1;
      arr.sort((a, b) => {
        const va = a[key] ?? "";
        const vb = b[key] ?? "";
        // numeric compare if both numbers
        const na = Number(va);
        const nb = Number(vb);
        if (!isNaN(na) && !isNaN(nb)) {
          return (na - nb) * dir;
        }
        const sa = String(va).toLowerCase();
        const sb = String(vb).toLowerCase();
        if (sa < sb) return -1 * dir;
        if (sa > sb) return 1 * dir;
        return 0;
      });
      return arr;
    },

    totalPages() {
      return Math.max(1, Math.ceil(this.sortedItems.length / this.pageSize));
    },

    visibleItems() {
      const start = (this.page - 1) * this.pageSize;
      return this.sortedItems.slice(start, start + this.pageSize);
    },
  },

  methods: {
    async login() {
      this.authError = null;
      try {
        // OAuth2PasswordRequestForm expects form fields: username, password
        const fd = new FormData();
        fd.append("username", this.username);
        fd.append("password", this.password);

        const res = await fetch("/api/v1/token/", {
          method: "POST",
          body: fd,
        });

        if (!res.ok) {
          const msg = await res.json().catch(() => null);
          this.authError = msg?.detail || `Ошибка входа (${res.status})`;
          return;
        }
        const json = await res.json();
        if (!json || !json.access_token) {
          this.authError = "Не получили токен авторизации";
          return;
        }

        this.token = json.access_token;
        localStorage.setItem("admin_token", this.token);
        this.isAuthenticated = true;
        this.username = "";
        this.password = "";
        this.authError = null;

        // загрузим данные
        await this.refreshItems();
      } catch (e) {
        console.error(e);
        this.authError = "Ошибка при соединении с сервером";
      }
    },

    logout() {
      this.token = null;
      this.isAuthenticated = false;
      localStorage.removeItem("admin_token");
      this.items = [];
    },

    async refreshItems() {
      this.uploadError = null;
      try {
        // читаем все items (увеличиваем лимит)
        // если backend накрывает пагинацией — можно вызывать несколько раз, здесь простой вариант
        const res = await fetch("/api/v1/items/?limit=10000", { method: "GET" });
        if (!res.ok) {
          if (res.status === 401 || res.status === 403) {
            // токен просрочен/нет доступа
            this.logout();
            this.authError = "Требуется авторизация (войдите снова)";
            return;
          }
          throw new Error(`Ошибка ${res.status}`);
        }
        const data = await res.json();
        // ожидаем: data = array of items
        this.items = Array.isArray(data) ? data : [];
        // переставим на первую страницу
        this.page = 1;
      } catch (e) {
        console.error(e);
        this.uploadError = "Не удалось загрузить список образцов";
      }
    },

    onFileSelected(e) {
      const f = e.target.files && e.target.files[0];
      if (!f) return;
      this.uploadExcel(f);
      // очистим input чтобы можно было выбрать тот же файл снова при необходимости
      this.$refs.fileInput.value = null;
    },

    async uploadExcel(file) {
      this.uploadError = null;
      this.uploadSuccess = false;
      if (!this.token) {
        this.uploadError = "Требуется авторизация для загрузки";
        return;
      }
      const fd = new FormData();
      fd.append("file", file);

      try {
        this.uploadInProgress = true;
        this.uploadProgressText = "Отправка...";
        const res = await fetch("/api/v1/items/", {
          method: "POST",
          body: fd,
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        });

        const bodyText = await res.text().catch(() => "");
        let json = null;
        try { json = JSON.parse(bodyText); } catch (_) { /* not json */ }

        if (!res.ok) {
          // если 401/403 — сбрасываем авторизацию
          if (res.status === 401 || res.status === 403) {
            this.logout();
            this.uploadError = "Требуется авторизация (войдите снова)";
            this.uploadInProgress = false;
            return;
          }
          this.uploadError = json?.detail || `Ошибка загрузки (${res.status})`;
          this.uploadInProgress = false;
          return;
        }

        // успех — response model ожидается schemas.InsertStatus
        this.uploadSuccess = true;
        this.uploadInProgress = false;
        this.uploadProgressText = "";
        // обновляем таблицу
        await this.refreshItems();
      } catch (e) {
        console.error(e);
        this.uploadError = "Ошибка при отправке файла на сервер";
        this.uploadInProgress = false;
        this.uploadProgressText = "";
      }
    },

    // сортировка
    setSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = key;
        this.sortDir = "asc";
      }
      this.page = 1;
    },

    numericOrDash(v) {
      if (v === null || v === undefined || v === "") return "-";
      if (!isNaN(Number(v))) return Number(v);
      return v;
    },

    // хендлер на успешную загрузку из компонента UploadExcel (при наличии)
    onUploadSuccess(resp) {
      // resp — arbitrary returned object from your UploadExcel
      this.uploadSuccess = true;
      this.uploadError = null;
      this.refreshItems();
    },
  },

  mounted() {
    // попытка восстановить токен из localStorage
    const t = localStorage.getItem("admin_token");
    if (t) {
      this.token = t;
      this.isAuthenticated = true;
      // сразу загрузим данные (если токен рабочий)
      this.refreshItems();
    }
  },
};
</script>

<style scoped>
.container { max-width:1100px; margin:16px auto; padding:12px; }

/* Карточки (фон белый) — заставим основной текст быть тёмным */
.card {
  padding:16px;
  border-radius:12px;
  background:white;
  box-shadow: 0 6px 18px rgba(0,0,0,0.04);
  margin-bottom:16px;

  /* основной цвет текста внутри карточек */
  color: #222222;
}

/* Уточнения: заголовки, подписи, параграфы внутри карточек */
.card h1,
.card h2,
.card h3,
.card p,
.card .hint,
.card label,
.card td,
.card th,
.card .form-row,
.card .login-form,
.card .table-controls,
.card .status-row {
  color: #222222;
}

/* Поля ввода — текст внутри input */
.card input,
.card textarea,
.card select {
  color: #222222;
  /* оставляем прежний вид рамок/фонов */
  background: transparent;
}

/* Логин-блок: чуть улучшение контраста заголовка */
.login-block { max-width:520px; }

/* Остальные правила оставляем как были, только убедимся, что ошибки/успехи не перекрываются */
.login-form .form-row { margin-bottom:10px; display:flex; flex-direction:column; gap:6px; }
.login-form input { padding:8px 10px; border-radius:8px; border:1px solid #ddd; color: #222222; }
.form-actions { margin-top:8px; }
.error { color:#b00020; margin-top:8px; }
.success { color: #0a8b3a; margin-top:8px; }
.admin-toolbar { display:flex; justify-content:space-between; align-items:center; gap:12px; margin-bottom:12px; }
.upload-actions { display:flex; gap:8px; align-items:center; }
.btn { padding:8px 12px; border-radius:8px; cursor:pointer; border:1px solid rgba(0,0,0,0.08); background:transparent; color: inherit; }
.btn-primary { background: var(--hp-button-color, #75E4F5); color: #022; border: none; }
.status-row { margin:8px 0 12px 0; min-height:20px; }

/* Таблица */
.items-table table { width:100%; border-collapse:collapse; font-size:13px; }
.items-table th, .items-table td { text-align:left; padding:8px 10px; border-bottom:1px solid rgba(0,0,0,0.04); color: #222222; }
.items-table th { cursor:pointer; user-select:none; background: rgba(0,0,0,0.02); }

/* Контролы таблицы */
.table-controls { display:flex; gap:8px; margin:8px 0 12px 0; align-items:center; }
.table-controls input { padding:8px; border-radius:8px; border:1px solid #ddd; color: #222222; }

/* Пейджинг */
.paging-lite { display:flex; justify-content:space-between; align-items:center; margin-top:12px; gap:12px; }
.page-nav { display:flex; gap:8px; align-items:center; }

/* Подсказки/пустое состояние */
.hint { color:#666; } /* подписи подсказок можно оставить серыми */
.no-items { padding:16px; text-align:center; }
</style>
